import { Controller } from "@hotwired/stimulus"
import { Sortable } from "sortablejs"
import Honeybadger from "@honeybadger-io/js"
import { fitCellsToHeader, orderCells } from "../helpers/column_order"

const PERSIST_DEBOUNCE_MS = 400
const KEY_STEP = 10
const KEY_STEP_MULTIPLIER = 5

// Excel-like column widths and drag reorder for a configurable list table.
//
// The DOM is the only state: <col style="width"> carries the widths and the <th data-column>
// order carries the column order, so a Turbo frame re-render always starts from server truth
// and reconnecting costs nothing. Every change PATCHes the FULL layout, which is also what
// makes the submitted order authoritative on the server.
//
// A `data-flexible` column (a position grid's Kurztext) takes whatever width the table has left:
// its <col> has no width, and what is stored is its minimum (`data-min-width`). Resizing it
// starts from the width it is drawn at. A `data-frozen` header stays in place like the pinned one.
export default class extends Controller {
  static targets = ["scroller", "col", "header", "headerRow", "handle", "grip"]
  static values = {
    layoutUrl: String,
    perPage: Number,
    minWidth: { type: Number, default: 60 },
    maxWidth: { type: Number, default: 800 },
    errorMessage: String,
  }

  connect() {
    this.table = this.element.querySelector("table")
    this.lastPersisted = this.layout()
    this.initializeSortable()
    this.onScroll = () => this.updateScrolledState()
    this.scrollerTarget.addEventListener("scroll", this.onScroll, { passive: true })
    this.updateScrolledState()
    this.onStreamRender = (event) => this.fitStreamedRows(event)
    document.addEventListener("turbo:before-stream-render", this.onStreamRender)
  }

  disconnect() {
    this.sortable?.destroy()
    this.scrollerTarget.removeEventListener("scroll", this.onScroll)
    document.removeEventListener("turbo:before-stream-render", this.onStreamRender)
    this.endResizeState()
    // A save still waiting out its debounce must not die with the frame — clicking another
    // tab right after a resize is the common case.
    if (this.persistTimer) {
      clearTimeout(this.persistTimer)
      this.persist({ keepalive: true })
    }
  }

  // -- layout <-> DOM ---------------------------------------------------------

  layout() {
    return {
      columns: this.colTargets.map((col) => ({
        key: col.dataset.column,
        width: this.widthOf(col),
      })),
      per_page: this.perPageValue,
    }
  }

  currentOrder() {
    return this.headerTargets.map((th) => th.dataset.column)
  }

  // Idempotent: brings the <col>s, the header row and every body and footer row (a totals
  // row) into `keys` order, with the filler cell last. Also used to roll a failed reorder back.
  applyOrder(keys) {
    const rows = [this.table.querySelector("colgroup"), this.headerRowTarget, ...this.ownRows()]
    rows.forEach((row) => orderCells(row, keys))
  }

  // A row streamed back into this table (a saved cell, an undo) is rendered in the layout stored
  // for the account — not this page's while a header drag's PATCH is still on its way, nor once
  // another tab has changed the layout. Its cells are fitted to this header before the swap, in
  // the stream's template (Turbo clones it only when rendering), so no live cell moves and an
  // editor keeps its focus.
  fitStreamedRows(event) {
    const stream = event.target
    if (!stream.targetElements?.some((target) => this.ownsStreamTarget(target))) return

    const keys = this.currentOrder()
    if (keys.length === 0) return
    stream.templateElement.content
      .querySelectorAll("tr")
      .forEach((row) => fitCellsToHeader(row, keys))
  }

  // The body and footer rows of THIS table. A row may hold a table of its own (the Spesenzentrale
  // opens an expense's Belegpositionen under its row), whose rows follow its own columns.
  ownRows() {
    return [...this.table.querySelectorAll("tbody tr, tfoot tr")].filter(
      (row) => row.closest("table") === this.table,
    )
  }

  // A stream into this table's rows — not into a table nested in one of them, nor into some other
  // element that merely sits inside a row.
  ownsStreamTarget(target) {
    return target.matches("tr, tbody, tfoot") && target.closest("table") === this.table
  }

  colFor(th) {
    return this.colTargets.find((col) => col.dataset.column === th.dataset.column)
  }

  widthOf(col) {
    return parseInt(col.dataset.flexible === undefined ? col.style.width : col.dataset.minWidth, 10)
  }

  // Where a resize starts: the stored width, or for a flexible column the width it is drawn at.
  drawnWidth(col) {
    if (col.dataset.flexible === undefined) return this.widthOf(col)
    const th = this.headerTargets.find((header) => header.dataset.column === col.dataset.column)
    return Math.round(th?.getBoundingClientRect().width || this.widthOf(col))
  }

  setWidth(col, width) {
    const clamped = Math.min(this.maxWidthValue, Math.max(this.minWidthValue, Math.round(width)))
    if (col.dataset.flexible === undefined) col.style.width = `${clamped}px`
    else col.dataset.minWidth = clamped
    // The pinned column's width is where a frozen one beside it sticks.
    if (col.hasAttribute("data-pinned-col"))
      this.table.style.setProperty("--grid-table-pinned-width", `${clamped}px`)
    // The fixed leading / trailing columns (bulk select, row actions) are no layout columns but
    // still take their width, as the server-rendered min-width counts them.
    const fixed = [...this.table.querySelectorAll("col[data-leading], col[data-trailing]")].reduce(
      (sum, entry) => sum + parseInt(entry.style.width, 10),
      0,
    )
    const total = this.colTargets.reduce((sum, entry) => sum + this.widthOf(entry), fixed)
    this.table.style.minWidth = `${total}px`
    const handle = this.handleTargets.find(
      (candidate) => candidate.closest("th").dataset.column === col.dataset.column,
    )
    handle?.setAttribute("aria-valuenow", clamped)
    return clamped
  }

  // -- resize (pointer) -------------------------------------------------------

  startResize(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return
    event.preventDefault()
    // The handle overhangs the neighbouring cell and SortableJS listens on the row, so the
    // event must not travel any further.
    event.stopPropagation()

    const handle = event.currentTarget
    const th = handle.closest("th")
    const col = this.colFor(th)
    if (!col) return

    const startWidth = this.drawnWidth(col)
    this.resizing = { handle, th, col, startX: event.clientX, startWidth, width: startWidth }
    handle.setPointerCapture(event.pointerId)
    handle.addEventListener("pointermove", this.onPointerMove)
    handle.addEventListener("pointerup", this.onPointerUp)
    handle.addEventListener("pointercancel", this.onPointerUp)
    this.element.dataset.resizing = ""
    th.dataset.resizing = ""
  }

  onPointerMove = (event) => {
    if (!this.resizing) return
    const delta = event.clientX - this.resizing.startX
    this.resizing.width = this.setWidth(this.resizing.col, this.resizing.startWidth + delta)
  }

  onPointerUp = () => {
    if (!this.resizing) return
    const { width, startWidth } = this.resizing
    this.endResizeState()
    if (width !== startWidth) this.schedulePersist()
  }

  endResizeState() {
    if (!this.resizing) return
    const { handle, th } = this.resizing
    handle.removeEventListener("pointermove", this.onPointerMove)
    handle.removeEventListener("pointerup", this.onPointerUp)
    handle.removeEventListener("pointercancel", this.onPointerUp)
    delete this.element.dataset.resizing
    delete th.dataset.resizing
    this.resizing = null
  }

  // -- resize (keyboard / double click) ---------------------------------------

  keyResize(event) {
    const step = { ArrowLeft: -KEY_STEP, ArrowRight: KEY_STEP }[event.key]
    if (step === undefined) return
    event.preventDefault()

    const col = this.colFor(event.currentTarget.closest("th"))
    if (!col) return
    const factor = event.shiftKey ? KEY_STEP_MULTIPLIER : 1
    this.setWidth(col, this.drawnWidth(col) + step * factor)
    this.schedulePersist()
  }

  resetWidth(event) {
    const col = this.colFor(event.currentTarget.closest("th"))
    if (!col) return
    this.setWidth(col, parseInt(col.dataset.defaultWidth, 10))
    this.schedulePersist()
  }

  // -- reorder ----------------------------------------------------------------

  initializeSortable() {
    this.sortable = new Sortable(this.headerRowTarget, {
      draggable: "th[data-column]:not([data-pinned]):not([data-frozen])",
      handle: '[data-grid-columns-target="grip"]',
      filter: ".grid-table-resize-handle, a",
      preventOnFilter: false, // the sort links stay clickable
      direction: "horizontal",
      animation: 150,
      ghostClass: "opacity-40",
      // One code path for mouse and touch; the native <th> drag image does neither well.
      forceFallback: true,
      fallbackOnBody: true,
      fallbackTolerance: 4,
      onStart: () => {
        this.previousOrder = this.currentOrder()
      },
      onEnd: () => {
        // SortableJS may drop the header after the filler or before the pinned (or a frozen)
        // column; those lead again, and applyOrder mirrors the result into every row.
        const fixed = this.headerTargets
          .filter((th) => th.hasAttribute("data-pinned") || th.hasAttribute("data-frozen"))
          .map((th) => th.dataset.column)
        const order = [...fixed, ...this.currentOrder().filter((key) => !fixed.includes(key))]
        this.applyOrder(order)
        if (order.join() !== this.previousOrder?.join()) this.persist()
      },
    })
  }

  // -- persistence ------------------------------------------------------------

  schedulePersist() {
    clearTimeout(this.persistTimer)
    this.persistTimer = setTimeout(() => this.persist(), PERSIST_DEBOUNCE_MS)
  }

  persist(fetchOptions = {}) {
    this.persistTimer = null
    const snapshot = { order: this.previousOrder, columns: this.lastPersisted.columns }
    const body = this.layout()

    return fetch(this.layoutUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')?.content,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      ...fetchOptions,
    })
      .then((response) => {
        // The session timed out while the page sat open (or Turbo restored it from cache):
        // nothing to report, the user just has to sign in again.
        if (response.status === 401) {
          Turbo.visit("/accounts/sign_in", { action: "replace" })
          return
        }
        if (!response.ok) throw new Error(`table layout save failed: ${response.status}`)
        this.lastPersisted = body
        this.previousOrder = null
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "grid-columns", url: this.layoutUrlValue },
        })
        this.rollback(snapshot)
      })
  }

  // Rolling back leaves the DOM equal to server truth again, so no reload is needed — and a
  // reload would have to guess the frame's URL, which in-frame searches make unreliable.
  rollback({ order, columns }) {
    if (order) this.applyOrder(order)
    for (const { key, width } of columns || []) {
      const col = this.colTargets.find((candidate) => candidate.dataset.column === key)
      if (col) this.setWidth(col, width)
    }
    this.showErrorToast()
  }

  showErrorToast() {
    if (!this.errorMessageValue) return

    const toast = document.createElement("div")
    toast.className =
      "fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg"
    toast.setAttribute("role", "status")
    toast.textContent = this.errorMessageValue
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }

  updateScrolledState() {
    this.element.toggleAttribute("data-scrolled-x", this.scrollerTarget.scrollLeft > 0)
  }
}
